import { Modal, ModalContent, ModalFooterWithActions } from '@/components/modal'
import { Link } from 'react-router-dom'
import { accountPaths } from '@/routing/paths'
import { DraftCorrespondenceSend, OpenNew, SlashCircle } from '@/assets/icons'
import type { OcrQuotaActionProps } from './OcrQuotaActions'

type OcrQuotaReachedModalProps = OcrQuotaActionProps & {
  open: boolean
  onDismiss: () => void
  variant: 'exhausted' | 'document-does-not-fit'
  remainingPages: number | null
  showUsage?: boolean
}

const numberFormatter = new Intl.NumberFormat()

export function OcrQuotaReachedModal({
  open,
  onDismiss,
  variant,
  remainingPages,
  onActivateSubscription,
  onContactSupport,
  showUsage = false,
}: OcrQuotaReachedModalProps) {
  const exhausted = variant === 'exhausted'
  const title = exhausted ? 'OCR page limit reached' : 'Document waiting for OCR quota'

  return (
    <Modal
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onDismiss()
      }}
    >
      <ModalContent
        title={title}
        titleIcon={<SlashCircle className="size-6 text-brand-neutral-700" aria-hidden />}
        width="md"
        height="max-full"
        showCloseButton
        footer={
          <ModalFooterWithActions
            secondaryAction={{ label: 'Not now', onClick: onDismiss }}
            primaryAction={
              onActivateSubscription
                ? {
                    label: 'Activate Subscription',
                    trailingIcon: <OpenNew className="size-4" aria-hidden />,
                    onClick: () => {
                      onDismiss()
                      onActivateSubscription()
                    },
                  }
                : {
                    label: 'Contact Support',
                    trailingIcon: <DraftCorrespondenceSend className="size-4" aria-hidden />,
                    onClick: () => {
                      onDismiss()
                      onContactSupport()
                    },
                  }
            }
          />
        }
      >
        <div className="space-y-3 text-sm text-brand-neutral-700">
          {exhausted ? (
            <p>This account has no OCR pages remaining. Any affected documents will remain queued until the allowance changes.</p>
          ) : (
            <p>
              {remainingPages === null
                ? 'At least one document does not fit within the remaining OCR allowance.'
                : `At least one document needs more pages than the ${numberFormatter.format(remainingPages)} pages currently remaining.`}{' '}
              It will remain queued until the allowance changes.
            </p>
          )}
          <p>Deleting files does not restore OCR pages.</p>
          {showUsage && (
            <Link
              to={accountPaths.usage}
              onClick={onDismiss}
              className="inline-block rounded font-medium text-text-link underline underline-offset-2 hover:text-link-text-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-link-focus-outline"
            >
              View Usage
            </Link>
          )}
        </div>
      </ModalContent>
    </Modal>
  )
}
